import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  Box,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  FormControl,
  Alert,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  isSafetyAlertUsageWearable,
} from '../../constants/wearableUsagePurpose';
import api from '../../services/api';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Pool, PoolUpdate } from '../../types';
import RiskLandThresholdsCard from '../system/RiskLandThresholdsCard';
import PoolCctvThresholdUnifyCard from './PoolCctvThresholdUnifyCard';
import {
  DEFAULT_RISK_LAND_THRESHOLDS,
  diffLandThresholdsFromBase,
  mergeRiskLandThresholds,
  type RiskLandThresholds,
} from '../../constants/riskLandThresholds';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  POOL_AI_DEFAULTS,
  AI_DETECTION_HINTS,
} from '../../constants/aiDetection';
import {
  applyPoolDetectionPreset,
  inferPoolDetectionPreset,
  type PoolDetectionPresetId,
  type DetectionLevelId,
} from '../../constants/detectionPresets';
import DetectionLevelPicker from '../system/DetectionLevelPicker';
import {
  POOL_RISK_MOTION_HINTS,
  POOL_RISK_MOTION_SECTION_INTRO,
  POOL_RISK_MOTION_TUNING_GUIDE,
  POOL_RISK_MOTION_HELPER_PROPS,
  mergePoolRiskMotionDefaults,
  type PoolRiskMotionDefaults,
} from '../../constants/poolRiskMotion';
import {
  applyMotionRiskPreset,
  inferMotionRiskPreset,
  type MotionRiskPresetId,
} from '../../constants/motionRiskPresets';
import {
  SAFETY_ALERT_CHANNEL_LABELS,
  type SafetyAlertChannelOverrides,
  overridesFromPool,
} from '../../constants/poolSafetyAlertChannels';
import { mergeSystemNotificationSettings } from '../../constants/notificationSettings';
import {
  fetchPoolSafetyAlertChannels,
  poolEffectiveSafetyChannels,
} from '../../services/poolSafetyAlertChannels';
import { SCENARIO_PROFILE_OPTIONS } from '../../constants/cctv';

export { POOL_AI_DEFAULTS };

/** 수영장 전용 weights 미지정 시 표시·선택 문구 (폼·조회 공통) */
export const POOL_DETECTOR_COMMON_LABEL = '공통 (시스템 기본)';

/** 시설별 추론 파이프라인 (빈 값 = 전역 monitoring_ai) */
const POOL_PIPELINE_OPTIONS: { value: string; label: string; detail: string }[] = [
  {
    value: '',
    label: '전역 설정 사용',
    detail: '시스템 설정의 추론 파이프라인을 따름.',
  },
  {
    value: 'dedicated',
    label: '전용 PT 단독 (권장)',
    detail: '학습한 weights 하나만 사용. COCO·하이브리드 보조 없음.',
  },
  {
    value: 'hybrid_recall',
    label: '전용 + COCO 리콜 보조',
    detail: '미검이 많을 때 전용+COCO 하이브리드.',
  },
  {
    value: 'coco_only_debug',
    label: 'COCO만 (디버그)',
    detail: 'yolov8n/s person 위주 — 전용 모델 검증·비교용.',
  },
  {
    value: 'raw_person_debug',
    label: '원본 YOLO (진단)',
    detail: '필터 없이 person만 — 모델 vs 필터 구분용(운영 금지).',
  },
];

interface PoolFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PoolUpdate) => Promise<void>;
  pool?: Pool;
  poolId?: number | string;
}

const PoolForm: React.FC<PoolFormProps> = ({ open, onClose, onSubmit, pool, poolId: poolIdProp }) => {
  const effectivePoolId = Number(pool?.id ?? poolIdProp ?? 0);
  const [formData, setFormData] = useState<PoolUpdate>({
    code: '',
    name: '',
    password: '',
    manager_name: '',
    manager_phone: '',
    safety_phone: '',
    email: '',
    location: '',
    address: '',
    capacity: 0,
    current_occupancy: 0,
    status: 'active',
    type: 'INDOOR',
    illuminance_sensor: false,
    external_photos: [],
    internal_photos: [],
    cctv_ips: [],
    monitor_ips: [],
    sirens: [],
    buzzers: [],
    is_active: true,
    dataSource: 'local',
    site_gateway_enabled: false,
    site_monitor_prefer_local: false,
    monitor_zone_tiles_only: false,
    gateway_stream_managed: false,
    gateway_nvr_bypass: true,
    gateway_stream_width: 640,
    gateway_stream_fps: 8,
    gateway_hls_upload: true,
    nvr_host: '',
    nvr_http_port: 80,
    nvr_rtsp_port: 554,
    nvr_username: '',
    nvr_password: '',
    nvr_vendor: 'hikvision',
    ai_detection_conf: POOL_AI_DEFAULTS.ai_detection_conf,
    ai_detection_min_consecutive_hits: POOL_AI_DEFAULTS.ai_detection_min_consecutive_hits,
    ai_detector_model: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [externalPhotosStr, setExternalPhotosStr] = useState('');
  const [internalPhotosStr, setInternalPhotosStr] = useState('');
  const [cctvIpsStr, setCctvIpsStr] = useState('');
  const [monitorIpsStr, setMonitorIpsStr] = useState('');
  const [riskMotion, setRiskMotion] = useState<PoolRiskMotionDefaults>(
    mergePoolRiskMotionDefaults()
  );
  const [systemLandBase, setSystemLandBase] = useState<RiskLandThresholds>(
    DEFAULT_RISK_LAND_THRESHOLDS
  );
  const [landThresholds, setLandThresholds] = useState<RiskLandThresholds>(
    DEFAULT_RISK_LAND_THRESHOLDS
  );
  const [poolDetectionPreset, setPoolDetectionPreset] =
    useState<PoolDetectionPresetId>('5');
  const [motionRiskPreset, setMotionRiskPreset] = useState<MotionRiskPresetId>('5');
  const [poolWearableOptions, setPoolWearableOptions] = useState<
    { device_id: string; label: string }[]
  >([]);
  const [wearablesLoading, setWearablesLoading] = useState(false);
  const [systemChannelDefaults, setSystemChannelDefaults] = useState<SafetyAlertChannelOverrides>({
    sms: true,
    kakao: false,
    wearable: true,
  });
  const [channelOverrides, setChannelOverrides] = useState<SafetyAlertChannelOverrides>({
    sms: true,
    kakao: false,
    wearable: true,
  });
  const [systemKakaoBotDefault, setSystemKakaoBotDefault] = useState(false);
  const [kakaoBotEnabled, setKakaoBotEnabled] = useState(false);
  const [yoloModelFiles, setYoloModelFiles] = useState<string[]>([]);
  const passwordTouchedRef = useRef(false);
  const detectionLevelRef = useRef<PoolDetectionPresetId>('5');
  const openSessionRef = useRef(false);
  /** 사용자가 8단계를 바꾼 뒤 도착한 DB GET이 덮어쓰지 않게 */
  const detectionTouchedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ files?: string[] }>('/api/settings/yolo-model-files');
        if (cancelled) return;
        setYoloModelFiles(Array.isArray(res?.files) ? res.files : []);
      } catch {
        if (!cancelled) setYoloModelFiles([]);
      }
    })();
    (async () => {
      try {
        const data = await api.get<{ risk_thresholds?: Partial<RiskLandThresholds> }>(
          '/api/settings/system'
        );
        if (cancelled) return;
        const base = mergeRiskLandThresholds(data?.risk_thresholds);
        setSystemLandBase(base);
      } catch {
        if (!cancelled) setSystemLandBase(DEFAULT_RISK_LAND_THRESHOLDS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLandThresholds(
      mergeRiskLandThresholds(systemLandBase, pool?.risk_land_thresholds)
    );
  }, [open, systemLandBase, pool?.id]);

  /** 수정 다이얼로그: DB에서 검출 성향·모델까지 다시 읽음 (캐시 금지) */
  useEffect(() => {
    if (!open) return;
    const pid = Number(pool?.id ?? poolIdProp);
    if (!Number.isFinite(pid) || pid <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const row = await api.get<Pool>(
          `/api/pools/${pid}`,
          { _: Date.now() },
          { useCache: false }
        );
        if (cancelled || !row) return;
        if (detectionTouchedRef.current) {
          // 모델·파이프라인만 갱신
          setFormData((prev) => ({
            ...prev,
            ai_detector_model: String(row.ai_detector_model ?? '').trim(),
            yolo_pipeline_preset: String(
              (row as { yolo_pipeline_preset?: string }).yolo_pipeline_preset ??
                prev.yolo_pipeline_preset ??
                ''
            ).trim(),
          }));
          return;
        }
        const conf =
          row.ai_detection_conf != null && row.ai_detection_conf !== undefined
            ? Number(row.ai_detection_conf)
            : POOL_AI_DEFAULTS.ai_detection_conf;
        const hits =
          row.ai_detection_min_consecutive_hits != null &&
          row.ai_detection_min_consecutive_hits !== undefined
            ? Number(row.ai_detection_min_consecutive_hits)
            : POOL_AI_DEFAULTS.ai_detection_min_consecutive_hits;
        const level = inferPoolDetectionPreset(conf, hits);
        detectionLevelRef.current = level;
        setPoolDetectionPreset(level);
        setFormData((prev) => ({
          ...prev,
          ai_detector_model: String(row.ai_detector_model ?? '').trim(),
          ai_detection_conf: conf,
          ai_detection_min_consecutive_hits: hits,
          yolo_pipeline_preset: String(
            (row as { yolo_pipeline_preset?: string }).yolo_pipeline_preset ?? ''
          ).trim(),
          scenario_profile: String(row.scenario_profile ?? prev.scenario_profile ?? 'pool').trim() || 'pool',
        }));
      } catch {
        /* pool prop 값 유지 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, pool?.id, poolIdProp]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const sysRes = await api.get('/api/settings/system');
        const notifications = mergeSystemNotificationSettings(
          (sysRes.data?.notifications as Record<string, unknown>) || undefined
        );
        const system: SafetyAlertChannelOverrides = {
          sms: notifications.safety_channel_sms,
          kakao: notifications.safety_channel_kakao,
          wearable: notifications.safety_channel_wearable,
        };
        const sysBot = Boolean(notifications.kakao_bot_send_enabled);
        if (cancelled) return;
        setSystemChannelDefaults(system);
        setSystemKakaoBotDefault(sysBot);
        const poolId = Number(pool?.id ?? poolIdProp);
        const storedOverrides = overridesFromPool(pool);
        const rawOv = pool?.safety_alert_channel_overrides as
          | Record<string, unknown>
          | null
          | undefined;
        if (Number.isFinite(poolId) && poolId > 0) {
          const ch = await fetchPoolSafetyAlertChannels(poolId);
          if (cancelled) return;
          setChannelOverrides(
            storedOverrides ?? poolEffectiveSafetyChannels(ch)
          );
          const botFromApi =
            typeof (ch as { kakao_bot?: boolean }).kakao_bot === 'boolean'
              ? Boolean((ch as { kakao_bot?: boolean }).kakao_bot)
              : undefined;
          const botFromPool =
            typeof rawOv?.kakao_bot === 'boolean' ? Boolean(rawOv.kakao_bot) : undefined;
          setKakaoBotEnabled(botFromApi ?? botFromPool ?? sysBot);
        } else {
          setChannelOverrides(storedOverrides ?? system);
          setKakaoBotEnabled(
            typeof rawOv?.kakao_bot === 'boolean' ? Boolean(rawOv.kakao_bot) : sysBot
          );
        }
      } catch {
        if (!cancelled) {
          const fromPool = overridesFromPool(pool);
          if (fromPool) setChannelOverrides(fromPool);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, pool?.id, poolIdProp, pool?.safety_alert_channel_overrides]);

  const setRiskMotionField = (field: keyof PoolRiskMotionDefaults, value: number) => {
    setMotionRiskPreset('custom');
    setRiskMotion((prev) => mergePoolRiskMotionDefaults({ ...prev, [field]: value }));
  };

  const setRiskSubtypeField = (
    key: keyof PoolRiskMotionDefaults['subtype_min_score'],
    value: number
  ) => {
    setMotionRiskPreset('custom');
    setRiskMotion((prev) =>
      mergePoolRiskMotionDefaults({
        ...prev,
        subtype_min_score: { ...prev.subtype_min_score, [key]: value },
      })
    );
  };

  /** 다이얼로그가 열릴 때만 pool로 초기화 — 편집 중 pool 참조 변경으로 8단계가 리셋되지 않게 */
  useEffect(() => {
    if (!open) {
      openSessionRef.current = false;
      return;
    }
    if (openSessionRef.current) return;
    openSessionRef.current = true;
    passwordTouchedRef.current = false;
    detectionTouchedRef.current = false;

    if (pool) {
      const conf = pool.ai_detection_conf ?? POOL_AI_DEFAULTS.ai_detection_conf;
      const hits =
        pool.ai_detection_min_consecutive_hits ??
        POOL_AI_DEFAULTS.ai_detection_min_consecutive_hits;
      const level = inferPoolDetectionPreset(conf, hits);
      detectionLevelRef.current = level;
      setRiskMotion(mergePoolRiskMotionDefaults(pool.risk_motion_defaults));
      setMotionRiskPreset(inferMotionRiskPreset(pool.risk_motion_defaults));
      setLandThresholds(
        mergeRiskLandThresholds(systemLandBase, pool.risk_land_thresholds)
      );
      setFormData({
        ...pool,
        password: '',
        nvr_password: '',
        nvr_host: pool.nvr_host || '',
        nvr_http_port: pool.nvr_http_port ?? 80,
        nvr_rtsp_port: pool.nvr_rtsp_port ?? 554,
        nvr_username: pool.nvr_username || '',
        nvr_vendor: pool.nvr_vendor || 'hikvision',
        dataSource: pool.dataSource || 'local',
        ai_detection_conf: conf,
        ai_detection_min_consecutive_hits: hits,
        ai_detector_model: String(pool.ai_detector_model ?? '').trim(),
        yolo_pipeline_preset: String(
          (pool as { yolo_pipeline_preset?: string }).yolo_pipeline_preset ?? ''
        ).trim(),
        scenario_profile: String(pool.scenario_profile ?? 'pool').trim() || 'pool',
      });
      setPoolDetectionPreset(level);
      setExternalPhotosStr(pool.external_photos?.join(', ') || '');
      setInternalPhotosStr(pool.internal_photos?.join(', ') || '');
      setCctvIpsStr(pool.cctv_ips?.join(', ') || '');
      setMonitorIpsStr(pool.monitor_ips?.join(', ') || '');
    } else {
      detectionLevelRef.current = '5';
      setRiskMotion(mergePoolRiskMotionDefaults());
      setMotionRiskPreset('5');
      setLandThresholds(mergeRiskLandThresholds(systemLandBase));
      setFormData({
        code: '',
        name: '',
        password: '',
        manager_name: '',
        manager_phone: '',
        safety_phone: '',
        safety_alert_wearable_ids: [],
        email: '',
        location: '',
        address: '',
        capacity: 0,
        current_occupancy: 0,
        status: 'active',
        type: 'INDOOR',
        illuminance_sensor: false,
        external_photos: [],
        internal_photos: [],
        cctv_ips: [],
        monitor_ips: [],
        sirens: [],
        buzzers: [],
        is_active: true,
        dataSource: 'local',
        site_gateway_enabled: false,
        site_monitor_prefer_local: false,
        monitor_zone_tiles_only: false,
        gateway_stream_managed: false,
        gateway_nvr_bypass: true,
        gateway_stream_width: 640,
        gateway_stream_fps: 8,
        gateway_hls_upload: true,
        nvr_host: '',
        nvr_http_port: 80,
        nvr_rtsp_port: 554,
        nvr_username: '',
        nvr_password: '',
        nvr_vendor: 'hikvision',
        ai_detection_conf: POOL_AI_DEFAULTS.ai_detection_conf,
        ai_detection_min_consecutive_hits: POOL_AI_DEFAULTS.ai_detection_min_consecutive_hits,
        ai_detector_model: '',
        yolo_pipeline_preset: '',
        scenario_profile: 'pool',
      });
      setPoolDetectionPreset('5');
      setExternalPhotosStr('');
      setInternalPhotosStr('');
      setCctvIpsStr('');
      setMonitorIpsStr('');
    }
  }, [open, pool]);

  useEffect(() => {
    const rawId = pool?.id ?? poolIdProp;
    if (!open || rawId == null || rawId === '') {
      setPoolWearableOptions([]);
      return;
    }
    const poolId = Number(rawId);
    if (!Number.isFinite(poolId)) return;
    let cancelled = false;
    setWearablesLoading(true);
    api
      .get<unknown[] | { data?: unknown[] }>(`/api/pools/${poolId}/wearables`)
      .then((body) => {
        if (cancelled) return;
        const rows = Array.isArray(body)
          ? body
          : Array.isArray(body?.data)
            ? body.data
            : [];
        setPoolWearableOptions(
          rows
            .filter((w: { device_id?: string; usage_purpose?: string }) => w?.device_id)
            .filter((w: { usage_purpose?: string }) =>
              isSafetyAlertUsageWearable(w.usage_purpose)
            )
            .map((w: { device_id: string; name?: string; code?: string; usage_purpose?: string }) => ({
              device_id: String(w.device_id),
              label: [w.name, w.code, w.device_id].filter(Boolean).join(' · '),
            }))
        );
      })
      .catch(() => {
        if (!cancelled) setPoolWearableOptions([]);
      })
      .finally(() => {
        if (!cancelled) setWearablesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pool?.id, poolIdProp]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target;
    if (type === 'checkbox') {
      const checked = (event.target as HTMLInputElement).checked;
      if (name === 'gateway_stream_managed' && checked) {
        setFormData((prev) => ({
          ...prev,
          gateway_stream_managed: true,
          gateway_nvr_bypass: prev.gateway_nvr_bypass ?? true,
          gateway_stream_width: prev.gateway_stream_width || 640,
          gateway_stream_fps: prev.gateway_stream_fps || 8,
        }));
        return;
      }
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      const floatFields = ['ai_detection_conf'];
      const intFields = ['ai_detection_min_consecutive_hits'];
      const nm = name as string;
      if (floatFields.includes(nm)) {
        const n = parseFloat(value);
        detectionTouchedRef.current = true;
        detectionLevelRef.current = 'custom';
        setPoolDetectionPreset('custom');
        setFormData((prev) => ({
          ...prev,
          [nm]:
            value === ''
              ? null
              : Number.isFinite(n)
                ? n
                : (prev as PoolUpdate).ai_detection_conf ?? POOL_AI_DEFAULTS.ai_detection_conf,
        }));
      } else if (intFields.includes(nm)) {
        const n = parseInt(value, 10);
        detectionTouchedRef.current = true;
        detectionLevelRef.current = 'custom';
        setPoolDetectionPreset('custom');
        setFormData((prev) => ({
          ...prev,
          [nm]:
            value === ''
              ? null
              : Number.isFinite(n)
                ? n
                : (prev as PoolUpdate).ai_detection_min_consecutive_hits ??
                  POOL_AI_DEFAULTS.ai_detection_min_consecutive_hits,
        }));
      } else if (type === 'number') {
        setFormData((prev) => ({
          ...prev,
          [name]: value === '' ? null : Number(value),
        }));
      } else {
        if (name === 'password') {
          passwordTouchedRef.current = true;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleListStringChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    // 입력값에서 연속된 쉼표를 하나로 변환하고, 앞뒤 공백을 제거
    const processedValue = value.replace(/,+/g, ',').trim();
    
    switch (name) {
      case 'external_photos_str': setExternalPhotosStr(processedValue); break;
      case 'internal_photos_str': setInternalPhotosStr(processedValue); break;
      case 'cctv_ips_str': setCctvIpsStr(processedValue); break;
      case 'monitor_ips_str': setMonitorIpsStr(processedValue); break;
      default: break;
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (name === 'gateway_stream_width') {
      const n = Number(value);
      setFormData((prev) => ({
        ...prev,
        gateway_stream_width: Number.isFinite(n) ? n : 640,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('수정 or 추가 여부:', pool ? '수정' : '추가');
    console.log('제출할 데이터 (상세):', formData);

    // 필수 필드 검증
    const requiredFields: (keyof PoolUpdate)[] = [
      'name', 'manager_name', 'manager_phone', 'safety_phone', 
      'email', 'location', 'address', 'capacity'
    ];
    
    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      alert(`다음 필드를 입력: ${missingFields.join(', ')}`);
      return;
    }

    try {
      // 8단계 ref 기준으로 conf/hits 확정 (편집 중 pool effect에 안 밀림)
      const level = detectionLevelRef.current;
      const detectionFields =
        level !== 'custom'
          ? applyPoolDetectionPreset(level as DetectionLevelId)
          : {
              ai_detection_conf: Number(
                formData.ai_detection_conf ?? POOL_AI_DEFAULTS.ai_detection_conf
              ),
              ai_detection_min_consecutive_hits: Number(
                formData.ai_detection_min_consecutive_hits ??
                  POOL_AI_DEFAULTS.ai_detection_min_consecutive_hits
              ),
            };

      const submitData: PoolUpdate & {
        _aiDetectionSave?: {
          ai_detection_conf: number;
          ai_detection_min_consecutive_hits: number;
          ai_detector_model: string | null;
          yolo_pipeline_preset: string | null;
        };
      } = {
        ...formData,
        ...detectionFields,
        risk_motion_defaults: riskMotion,
        risk_land_thresholds: diffLandThresholdsFromBase(landThresholds, systemLandBase),
        safety_alert_channel_overrides: {
          ...channelOverrides,
          kakao_bot: kakaoBotEnabled,
        },
        external_photos: externalPhotosStr.split(',').map(s => s.trim()).filter(s => s.length > 0),
        internal_photos: internalPhotosStr.split(',').map(s => s.trim()).filter(s => s.length > 0),
        cctv_ips: cctvIpsStr.split(',').map(s => s.trim()).filter(s => s.length > 0),
        monitor_ips: monitorIpsStr.split(',').map(s => s.trim()).filter(s => s.length > 0),
      };

      const raw = (submitData.ai_detector_model ?? '').trim();
      submitData.ai_detector_model = raw.length > 0 ? raw : null;
      const pipe = String(
        (submitData as { yolo_pipeline_preset?: string }).yolo_pipeline_preset ?? ''
      ).trim();
      (submitData as { yolo_pipeline_preset?: string | null }).yolo_pipeline_preset =
        pipe.length > 0 ? pipe : null;
      submitData.scenario_profile =
        String(submitData.scenario_profile || 'pool').trim() || 'pool';

      submitData._aiDetectionSave = {
        ai_detection_conf: detectionFields.ai_detection_conf,
        ai_detection_min_consecutive_hits: detectionFields.ai_detection_min_consecutive_hits,
        ai_detector_model: submitData.ai_detector_model ?? null,
        yolo_pipeline_preset:
          (submitData as { yolo_pipeline_preset?: string | null }).yolo_pipeline_preset ?? null,
      };

      if (pool) {
        if (!passwordTouchedRef.current || !String(submitData.password || '').trim()) {
          delete submitData.password;
        }
      }
      if (!String(submitData.nvr_password || '').trim()) {
        delete submitData.nvr_password;
      }

      delete (submitData as { dataSource?: unknown }).dataSource;
      delete (submitData as { id?: unknown }).id;
      delete (submitData as { created_at?: unknown }).created_at;
      delete (submitData as { updated_at?: unknown }).updated_at;

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('시설 저장 중 오류 발생:', error);
      alert('시설 저장 중 오류 발생');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const detectorModelValue = String(formData.ai_detector_model ?? '').trim();

  const renderDetectorModelValue = (selected: unknown) => {
    const s = String(selected ?? '').trim();
    if (!s) return POOL_DETECTOR_COMMON_LABEL;
    if (!yoloModelFiles.includes(s)) return `${s} (목록에 없음)`;
    return s;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{pool ? '시설 수정' : '새 시설 추가'}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField required fullWidth label="시설 코드 (필수)" name="code" value={formData.code || ''} onChange={handleChange} margin="normal" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField required fullWidth label="시설 이름 (필수)" name="name" value={formData.name || ''} onChange={handleChange} margin="normal" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl required fullWidth margin="normal">
                <InputLabel id="pool-type-label">실내/실외 (필수)</InputLabel>
                <Select labelId="pool-type-label" name="type" value={formData.type || 'INDOOR'} onChange={handleSelectChange} label="실내/실외 (필수)">
                  <MenuItem value="INDOOR">실내</MenuItem>
                  <MenuItem value="OUTDOOR">실외</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl required fullWidth margin="normal">
                <InputLabel id="scenario-profile-label">시설 형태(관제 프로필) (필수)</InputLabel>
                <Select
                  labelId="scenario-profile-label"
                  name="scenario_profile"
                  value={formData.scenario_profile || 'pool'}
                  onChange={handleSelectChange}
                  label="시설 형태(관제 프로필) (필수)"
                >
                  {SCENARIO_PROFILE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.group ? `[${opt.group}] ${opt.label}` : opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5, mb: 0.5 }}>
                시설 단위 설정. 정식 수영장(익사)·물놀이장 등을 고르면, CCTV의 「검출 용도」「위험 시나리오」 선택 목록이 그에 맞게 필터됨.
                (검출 용도·시나리오는 CCTV 추가/수정 화면에서 설정.)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField 
                required 
                fullWidth 
                label="위치 설명 (필수)" 
                name="location" 
                value={formData.location || ''} 
                onChange={handleChange} 
                margin="normal"
                error={!formData.location}
                helperText={!formData.location ? "위치 설명을 입력" : ""} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField 
                required 
                fullWidth 
                label="주소 (필수)" 
                name="address" 
                value={formData.address || ''} 
                onChange={handleChange} 
                margin="normal"
                error={!formData.address}
                helperText={!formData.address ? "주소를 입력" : ""} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField 
                required 
                fullWidth 
                label="담당자 이름 (필수)" 
                name="manager_name" 
                value={formData.manager_name || ''} 
                onChange={handleChange} 
                margin="normal"
                error={!formData.manager_name}
                helperText={!formData.manager_name ? "담당자 이름을 입력" : ""} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField 
                required 
                fullWidth 
                label="담당자 전화번호 (필수)" 
                name="manager_phone" 
                value={formData.manager_phone || ''} 
                onChange={handleChange} 
                margin="normal"
                error={!formData.manager_phone}
                helperText={!formData.manager_phone ? "담당자 전화번호를 입력" : ""} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="현장 안전관리자 연락처"
                name="safety_phone"
                value={formData.safety_phone || ''}
                onChange={handleChange}
                margin="normal"
                placeholder="홍길동:01012345678, 김철수:01098765432"
                helperText="쉼표(,)로 구분. SMS는 번호 사용. 카카오 봇은 PC 검색용으로 이름:번호 형식 권장 (이름만으로 친구 검색)."
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                현장 안전 알림 채널 (시설 기본)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                메인 설정 알림 채널을 기본으로 하며, 시설마다 경고/위험 알림 전송 창의 체크 기본값이 됨.
                (시스템: SMS {systemChannelDefaults.sms ? 'ON' : 'OFF'} · 카카오{' '}
                {systemChannelDefaults.kakao ? 'ON' : 'OFF'} · 웨어러블{' '}
                {systemChannelDefaults.wearable ? 'ON' : 'OFF'} · 카카오 봇{' '}
                {systemKakaoBotDefault ? 'ON' : 'OFF'})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(['sms', 'kakao', 'wearable'] as const).map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        checked={channelOverrides[key]}
                        onChange={(e) =>
                          setChannelOverrides((prev) => ({ ...prev, [key]: e.target.checked }))
                        }
                      />
                    }
                    label={SAFETY_ALERT_CHANNEL_LABELS[key]}
                  />
                ))}
                <FormControlLabel
                  control={
                    <Switch
                      checked={kakaoBotEnabled}
                      onChange={(e) => setKakaoBotEnabled(e.target.checked)}
                    />
                  }
                  label="카카오 봇 보내기"
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                카카오 봇: 이벤트 전송 시 PC 카카오톡으로 이미지 자동 전송. 연락처는 이름:번호 형식 권장.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={poolWearableOptions}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(a, b) => a.device_id === b.device_id}
                value={poolWearableOptions.filter((opt) =>
                  (formData.safety_alert_wearable_ids || []).includes(opt.device_id)
                )}
                onChange={(_, selected) => {
                  setFormData((prev) => ({
                    ...prev,
                    safety_alert_wearable_ids: selected.map((s) => s.device_id),
                  }));
                }}
                loading={wearablesLoading}
                disabled={!(pool?.id ?? poolIdProp)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.label}
                      size="small"
                      {...getTagProps({ index })}
                      key={option.device_id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    label="알림용 웨어러블"
                    placeholder={
                      pool?.id ?? poolIdProp
                        ? '이 시설에 등록된 웨어러블 중 선택'
                        : '시설 저장 후 기기를 선택 가능'
                    }
                    helperText="「알림용」으로 등록된 웨어러블만 선택 가능. 웨어러블 관리에서 용도를 지정."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField required fullWidth label="이메일" name="email" type="email" value={formData.email || ''} onChange={handleChange} margin="normal" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                required={!pool}
                fullWidth
                label={pool ? '비밀번호 (변경 시에만 입력)' : '비밀번호'}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password || ''}
                onChange={handleChange}
                margin="normal"
                autoComplete="new-password"
                helperText={
                  pool
                    ? '비워 두면 기존 비밀번호를 유지. 입력한 값으로 시설 /코드 접근 인증에 사용됨.'
                    : undefined
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="pool-status-label">상태</InputLabel>
                <Select labelId="pool-status-label" name="status" value={formData.status || 'active'} onChange={handleSelectChange} label="상태">
                  <MenuItem value="active">활성</MenuItem>
                  <MenuItem value="inactive">비활성</MenuItem>
                  <MenuItem value="maintenance">유지보수</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={<Switch checked={formData.illuminance_sensor ?? false} onChange={handleChange} name="illuminance_sensor" />}
                label="조도 센서 유무"
                sx={{ mt: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.site_gateway_enabled ?? false}
                    onChange={handleChange}
                    name="site_gateway_enabled"
                  />
                }
                label="라이브 (현장 게이트웨이)"
                sx={{ mt: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mb: 1 }}>
                ON = 실제 라이브: 현장 PoolGateway/go2rtc의 playback_url로 관제·AI·증거를 씀.
                (시스템 설정의 「실운영 playback_url 사용」도 함께 켬. 페어링 코드는 시설 정보에서 발급.)
                OFF = 목업: CCTV stream_url(예: localhost:8500 파일 HLS)을 그대로 씀. D:\hls_stream 은
                OFF일 때만 사용
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.site_monitor_prefer_local ?? false}
                    onChange={handleChange}
                    name="site_monitor_prefer_local"
                  />
                }
                label="시설 관제 모니터 — 현장 RTSP/직접 (게이트웨이 대신)"
                sx={{ mt: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mb: 1 }}>
                ON = 시설 경로(/시설코드) 관제만 stream_url(RTSP)을 씀. CCTV 등록 미리보기와 같이
                현장 PoolGateway 로컬 스냅샷(또는 LAN HLS)이라 WAN 영상 부하를 줄임.
                OFF = 시설에서도 관제실과 동일하게 중앙 게이트웨이(playback)를 봄.
                관제실(?key) 접근에는 영향 없음.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.monitor_zone_tiles_only ?? false}
                    onChange={handleChange}
                    name="monitor_zone_tiles_only"
                  />
                }
                label="관제 화면 — 감시구역 박스만 표시"
                sx={{ mt: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mb: 1 }}>
                ON = 중앙 관제에서 물 영역(감시구역) 박스가 있는 CCTV만 보이고, 구역마다 크롭 타일.
                OFF = 등록된 CCTV 전체 화면을 그대로 표시.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.gateway_stream_managed ?? false}
                    onChange={handleChange}
                    name="gateway_stream_managed"
                  />
                }
                label="전송 화질을 이 시설 설정에서 지정"
                sx={{ mt: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mb: 1 }}>
                ON이면 약 30초 안에 현장 게이트웨이 화면과 전송이 이 값으로 바뀜.
                현장에서 해상도·FPS를 바꿔도 이 설정이 다시 덮어씀.
                OFF이면 현장 화면의 값을 유지.
              </Typography>
              {formData.gateway_stream_managed ? (
                <Box sx={{ ml: 4.5, mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.gateway_nvr_bypass ?? true}
                        onChange={handleChange}
                        name="gateway_nvr_bypass"
                      />
                    }
                    label="원본 전송 (재인코딩 없음 · RTSP 그대로 copy)"
                  />
                  {formData.gateway_nvr_bypass ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      카메라/NVR RTSP를 그대로 WHIP. 아래 해상도·FPS는 미적용. 메인(예: 1280x720) 권장.
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth margin="dense">
                          <InputLabel id="gw-width-label">해상도 (가로 px)</InputLabel>
                          <Select
                            labelId="gw-width-label"
                            name="gateway_stream_width"
                            value={String(formData.gateway_stream_width || 1280)}
                            label="해상도 (가로 px)"
                            onChange={handleSelectChange}
                          >
                            <MenuItem value="640">640</MenuItem>
                            <MenuItem value="960">960</MenuItem>
                            <MenuItem value="1280">1280 (720p)</MenuItem>
                            <MenuItem value="1920">1920</MenuItem>
                            <MenuItem value="720">720 (비권장)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          margin="dense"
                          name="gateway_stream_fps"
                          label="FPS"
                          type="number"
                          value={formData.gateway_stream_fps ?? 8}
                          onChange={handleChange}
                          inputProps={{ min: 0.5, max: 30, step: 0.5 }}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              ) : null}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.gateway_hls_upload ?? true}
                    onChange={handleChange}
                    name="gateway_hls_upload"
                  />
                }
                label="HLS 세그먼트를 중앙 서버로 업로드"
                sx={{ mt: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mb: 1 }}>
                ON = 현장이 .ts / m3u8 을 중앙으로 올림(관제 WebRTC 백업·일부 상태). 업링크 추가 사용.
                OFF = 업로드 중지. 관제 본선(WebRTC/WHIP)과 증거 영상(현장 링버퍼→MP4)은 그대로.
                약 30초 안에 현장 게이트웨이에 반영. 증거와는 무관.
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                시설 NVR (라벨링 영상 가져오기)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                현장 게이트웨이가 이 NVR에 LAN으로 접속해 지난 구간을 가져옴.
                중앙 서버가 시설 LAN에 직접 붙지 않음. 현장 게이트웨이 ON + 페어링 필요.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="dense"
                    name="nvr_host"
                    label="NVR IP"
                    value={formData.nvr_host || ''}
                    onChange={handleChange}
                    placeholder="예: 192.168.0.100"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    margin="dense"
                    name="nvr_http_port"
                    label="HTTP 포트"
                    type="number"
                    value={formData.nvr_http_port ?? 80}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    margin="dense"
                    name="nvr_rtsp_port"
                    label="RTSP 포트"
                    type="number"
                    value={formData.nvr_rtsp_port ?? 554}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel id="nvr-vendor-label">제조사</InputLabel>
                    <Select
                      labelId="nvr-vendor-label"
                      name="nvr_vendor"
                      value={formData.nvr_vendor || 'hikvision'}
                      label="제조사"
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="hikvision">Hikvision / 호환</MenuItem>
                      <MenuItem value="dahua">Dahua / 호환</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    margin="dense"
                    name="nvr_username"
                    label="NVR 계정"
                    value={formData.nvr_username || ''}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    margin="dense"
                    name="nvr_password"
                    label={
                      pool?.nvr_password_set
                        ? 'NVR 비밀번호 (저장됨)'
                        : 'NVR 비밀번호'
                    }
                    type="password"
                    value={formData.nvr_password || ''}
                    onChange={handleChange}
                    autoComplete="new-password"
                    placeholder={
                      pool?.nvr_password_set ? '••••••••' : undefined
                    }
                    helperText={
                      pool?.nvr_password_set
                        ? '이미 저장됨. 보안상 값은 다시 보여 주지 않음. 바꿀 때만 입력, 비우면 유지'
                        : 'NVR 웹/앱 로그인과 동일 계정'
                    }
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                {`NVR에서 확인할 항목
· 녹화(연속/스케줄)가 켜져 있고, 가져올 날짜의 영상이 NVR에 남아 있어야 함
· RTSP·재생(Playback) 서비스가 켜져 있어야 함 (기본 포트 554)
· 원격 재생/다운로드 또는 ISAPI·SDK 재생이 허용된 계정인지 확인
· 현장 PC(게이트웨이) IP가 NVR에 접근 가능한지 확인 (같은 LAN, 방화벽 허용)
· Hikvision: 채널 1 → 트랙 101 형식. 영상 녹화 화면에서 카메라별 NVR 채널 번호를 맞춤
· Dahua: 채널 번호가 NVR 채널과 같아야 함
· 계정 잠금·2차 인증이 있으면 가져오기가 실패할 수 있음`}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                margin="normal"
                name="capacity"
                label="수용 인원(명) (필수)"
                value={formData.capacity || ''}
                onChange={handleChange}
                type="number"
                required
                error={formData.capacity === undefined || formData.capacity === null || formData.capacity <= 0}
                helperText={formData.capacity === undefined || formData.capacity === null || formData.capacity <= 0 ? "수용 인원은 1명 이상" : ""}
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField 
                fullWidth 
                label="현재 인원" 
                name="current_occupancy" 
                type="number" 
                value={formData.current_occupancy ?? 0} 
                onChange={handleChange} 
                margin="normal" 
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                영상 AI 감지 (이 시설만)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {AI_DETECTION_HINTS.poolFallback} 보통은 「5단계 · 균형」 선택. CCTV마다 다르면 CCTV
                관리에서 조정
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <DetectionLevelPicker
                variant="pool"
                legend="검출 성향 · 8단계"
                layout="select"
                value={poolDetectionPreset === 'custom' ? 'custom' : poolDetectionPreset}
                onSelectLevel={(level: DetectionLevelId) => {
                  detectionTouchedRef.current = true;
                  detectionLevelRef.current = level;
                  setPoolDetectionPreset(level);
                  const p = applyPoolDetectionPreset(level);
                  setFormData((prev) => ({
                    ...prev,
                    ai_detection_conf: p.ai_detection_conf,
                    ai_detection_min_consecutive_hits: p.ai_detection_min_consecutive_hits,
                  }));
                }}
                onSelectCustom={() => {
                  detectionTouchedRef.current = true;
                  detectionLevelRef.current = 'custom';
                  setPoolDetectionPreset('custom');
                }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                단계를 바꾸면 아래 「검출 신뢰도」「연속 프레임」 수치가 함께 갱신됨.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                margin="normal"
                name="ai_detection_conf"
                label="검출 신뢰도 하한"
                type="number"
                value={formData.ai_detection_conf ?? POOL_AI_DEFAULTS.ai_detection_conf}
                onChange={handleChange}
                inputProps={{ step: 0.05, min: 0.05, max: 0.99 }}
                helperText={AI_DETECTION_HINTS.conf}
                FormHelperTextProps={{ sx: { whiteSpace: 'pre-line' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                margin="normal"
                name="ai_detection_min_consecutive_hits"
                label="연속 프레임 확정"
                type="number"
                value={
                  formData.ai_detection_min_consecutive_hits ??
                  POOL_AI_DEFAULTS.ai_detection_min_consecutive_hits
                }
                onChange={handleChange}
                inputProps={{ step: 1, min: 1, max: 30 }}
                helperText={AI_DETECTION_HINTS.minHits}
                FormHelperTextProps={{ sx: { whiteSpace: 'pre-line' } }}
              />
            </Grid>
            {poolDetectionPreset === 'custom' && (
              <Grid item xs={12}>
                <Typography variant="caption" color="warning.main">
                  숫자를 직접 수정한 상태. 위에서 단계를 다시 고르면 일괄 적용됨.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel id="pool-ai-detector-model-label" shrink>
                  전용 검출 모델
                </InputLabel>
                <Select
                  labelId="pool-ai-detector-model-label"
                  label="전용 검출 모델"
                  name="ai_detector_model"
                  displayEmpty
                  notched
                  renderValue={renderDetectorModelValue}
                  value={detectorModelValue}
                  onChange={(e: SelectChangeEvent<string>) => {
                    const v = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      ai_detector_model: v === '' ? '' : v,
                    }));
                  }}
                >
                  <MenuItem value="">{POOL_DETECTOR_COMMON_LABEL}</MenuItem>
                  {detectorModelValue && !yoloModelFiles.includes(detectorModelValue) ? (
                    <MenuItem value={detectorModelValue}>
                      {detectorModelValue} (목록에 없음)
                    </MenuItem>
                  ) : null}
                  {yoloModelFiles.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                  {AI_DETECTION_HINTS.poolDetectorModel}
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel id="pool-yolo-pipeline-label" shrink>
                  추론 파이프라인
                </InputLabel>
                <Select
                  labelId="pool-yolo-pipeline-label"
                  label="추론 파이프라인"
                  name="yolo_pipeline_preset"
                  displayEmpty
                  notched
                  value={String(
                    (formData as { yolo_pipeline_preset?: string }).yolo_pipeline_preset ?? ''
                  )}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setFormData((prev) => ({
                      ...prev,
                      yolo_pipeline_preset: e.target.value,
                    }));
                  }}
                >
                  {POOL_PIPELINE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || '__global__'} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1, whiteSpace: 'pre-line' }}
                >
                  {POOL_PIPELINE_OPTIONS.find(
                    (o) =>
                      o.value ===
                      String(
                        (formData as { yolo_pipeline_preset?: string }).yolo_pipeline_preset ?? ''
                      )
                  )?.detail ||
                    '이 시설의.pt와 함께 쓰. CCTV는 시설 파이프라인을 그대로 따름.'}
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Motion 위험 감지 (시설 공통 · CCTV 기본값)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {POOL_RISK_MOTION_SECTION_INTRO} CCTV 관리 탭에서 카메라별로 「시설 motion 기본값
                사용」을 켜 두면, 이 섹션의 민감도·정지·유형 임계가 그 카메라에 적용됨.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" component="div">
                  {POOL_RISK_MOTION_TUNING_GUIDE}
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <DetectionLevelPicker
                variant="motion"
                legend="Motion 검출 성향 · 8단계"
                layout="select"
                value={motionRiskPreset === 'custom' ? 'custom' : motionRiskPreset}
                onSelectLevel={(level: DetectionLevelId) => {
                  setMotionRiskPreset(level);
                  setRiskMotion(mergePoolRiskMotionDefaults(applyMotionRiskPreset(level)));
                }}
                onSelectCustom={() => setMotionRiskPreset('custom')}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                단계를 바꾸면 아래 민감도·정지·통합·유형 임계 수치가 함께 갱신됨. CCTV에서 「시설
                motion 기본값 사용」을 켠 카메라에 적용됨
              </Typography>
            </Grid>
            {motionRiskPreset === 'custom' && (
              <Grid item xs={12}>
                <Typography variant="caption" color="warning.main">
                  숫자를 직접 수정한 상태. 위에서 단계를 다시 고르면 일괄 적용됨.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                label="민감도 (0~1)"
                type="number"
                value={riskMotion.sensitivity}
                onChange={(e) => setRiskMotionField('sensitivity', Number(e.target.value))}
                inputProps={{ step: 0.05, min: 0, max: 1 }}
                helperText={POOL_RISK_MOTION_HINTS.sensitivity}
                FormHelperTextProps={POOL_RISK_MOTION_HELPER_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                label="정지 기준(초)"
                type="number"
                value={riskMotion.stillness_threshold_sec}
                onChange={(e) =>
                  setRiskMotionField('stillness_threshold_sec', Number(e.target.value))
                }
                inputProps={{ step: 1, min: 4 }}
                helperText={POOL_RISK_MOTION_HINTS.stillness}
                FormHelperTextProps={POOL_RISK_MOTION_HELPER_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                label="통합 점수 임계"
                type="number"
                value={riskMotion.combined_score_threshold}
                onChange={(e) =>
                  setRiskMotionField('combined_score_threshold', Number(e.target.value))
                }
                inputProps={{ step: 1, min: 12, max: 60 }}
                helperText={POOL_RISK_MOTION_HINTS.combined}
                FormHelperTextProps={POOL_RISK_MOTION_HELPER_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                label="패턴(레거시) 임계"
                type="number"
                value={riskMotion.legacy_detection_threshold}
                onChange={(e) =>
                  setRiskMotionField('legacy_detection_threshold', Number(e.target.value))
                }
                inputProps={{ step: 0.1, min: 1.5, max: 8 }}
                helperText={POOL_RISK_MOTION_HINTS.legacy}
                FormHelperTextProps={POOL_RISK_MOTION_HELPER_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                label="유형 하한 · 익사"
                type="number"
                value={riskMotion.subtype_min_score.drowning}
                onChange={(e) => setRiskSubtypeField('drowning', Number(e.target.value))}
                inputProps={{ step: 0.05, min: 0.1, max: 0.9 }}
                helperText={POOL_RISK_MOTION_HINTS.subtypeDrowning}
                FormHelperTextProps={POOL_RISK_MOTION_HELPER_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                label="유형 하한 · 움직임 없음"
                type="number"
                value={riskMotion.subtype_min_score.no_motion}
                onChange={(e) => setRiskSubtypeField('no_motion', Number(e.target.value))}
                inputProps={{ step: 0.05, min: 0.1, max: 0.9 }}
                helperText={POOL_RISK_MOTION_HINTS.subtypeNoMotion}
                FormHelperTextProps={POOL_RISK_MOTION_HELPER_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                label="유형 하한 · 가라앉음"
                type="number"
                value={riskMotion.subtype_min_score.sinking}
                onChange={(e) => setRiskSubtypeField('sinking', Number(e.target.value))}
                inputProps={{ step: 0.05, min: 0.1, max: 0.9 }}
                helperText={POOL_RISK_MOTION_HINTS.subtypeSinking}
                FormHelperTextProps={POOL_RISK_MOTION_HELPER_PROPS}
              />
            </Grid>
            <Grid item xs={12}>
              <RiskLandThresholdsCard
                title="육상·시나리오 위험 임계값 (이 시설)"
                description="시스템 「임계 설정」 기본을 승계. 수정한 항목만 시설에 저장되고, 나머지는 시스템 변경 시 함께 따라감. 익사 motion 민감도/정지와는 별개."
                value={landThresholds}
                onChange={setLandThresholds}
                systemBase={systemLandBase}
                showActions={false}
              />
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => setLandThresholds(mergeRiskLandThresholds(systemLandBase))}
                >
                  시스템 기본으로 되돌리기
                </Button>
              </Box>
              {effectivePoolId > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <PoolCctvThresholdUnifyCard
                    poolId={effectivePoolId}
                    poolName={String(formData.name || pool?.name || '')}
                    specialZoneDwellSec={landThresholds.special_zone_dwell_sec}
                    detectionConfLabel={Number(
                      formData.ai_detection_conf ?? POOL_AI_DEFAULTS.ai_detection_conf
                    ).toFixed(2)}
                    detectionHitsLabel={String(
                      formData.ai_detection_min_consecutive_hits ??
                        POOL_AI_DEFAULTS.ai_detection_min_consecutive_hits
                    )}
                  />
                </Box>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="외부 사진 URL 목록 (쉼표로 구분)" name="external_photos_str" value={externalPhotosStr} onChange={handleListStringChange} margin="normal" helperText="예: https://example.com/photo1.jpg, https://example.com/photo2.jpg" />
            </Grid>
             <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="내부 사진 URL 목록 (쉼표로 구분)" name="internal_photos_str" value={internalPhotosStr} onChange={handleListStringChange} margin="normal" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline rows={2} label="CCTV IP 목록 (쉼표로 구분)" name="cctv_ips_str" value={cctvIpsStr} onChange={handleListStringChange} margin="normal" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline rows={2} label="모니터 IP 목록 (쉼표로 구분)" name="monitor_ips_str" value={monitorIpsStr} onChange={handleListStringChange} margin="normal" />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                경광등·부저는 「시설 관리 → 경광등/부저」 또는 「경광등/부저 A」 탭에서 등록하고,
                탭 상단 「이 방식으로 적용」으로 시설당 한 가지 방식만 선택.
              </Alert>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {pool ? '수정' : '추가'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PoolForm; 